import { useState, useCallback, useEffect, useRef } from 'react'
import { useRouter, useSearchParams } from '@/routing/navigation'
import { useLocation } from 'react-router-dom'
import { useSWRConfig } from 'swr'
import { toast } from 'sonner'
import { PageContent } from '@/components/layout/PageContent'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/input'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { Search, X, Plus, MessageSquare } from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'
import { Skeleton } from '@design-system/components/skeleton'
import { chatRoomsApi } from '@/lib/api'
import { revalidateConversationLists } from '@/lib/chat/revalidateConversationLists'
import { ROUTES } from '@/routes/routes'
import { useConversationHistory } from './useConversationHistory'
import { ConversationCard } from './ConversationCard'
import { ComposeModal } from './ComposeModal/ComposeModal'
import { InitialTwin } from './ComposeModal/useComposeModal'
import { useTranslation } from 'react-i18next'

export default function ConversationsPage() {
  const { t } = useTranslation('app')
  const router = useRouter()
  const searchParams = useSearchParams()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeInitialTwin, setComposeInitialTwin] = useState<InitialTwin | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const handleComposeOpenChange = useCallback((open: boolean) => {
    setComposeOpen(open)
    if (!open) {
      setComposeInitialTwin(null)
    }
  }, [])

  const locationState = location.state as { initialTwin?: InitialTwin; initialMessage?: string; groupMode?: boolean } | null
  const initialTwin = locationState?.initialTwin ?? null
  const initialMessage = locationState?.initialMessage ?? null
  const groupMode = locationState?.groupMode ?? false

  const hasHandledNewParam = useRef(false)
  useEffect(() => {
    if (searchParams.get('new') === '1' && !hasHandledNewParam.current) {
      hasHandledNewParam.current = true
      setComposeOpen(true)
      router.replace(ROUTES.chats)
    }
  }, [searchParams, router])

  const hasHandledInvalidRoomParam = useRef(false)
  useEffect(() => {
    if (searchParams.get('invalidRoom') !== '1' || hasHandledInvalidRoomParam.current) {
      return
    }
    hasHandledInvalidRoomParam.current = true
    toast.error(t('conversations.invalidRoomLink'))
    router.replace(ROUTES.chats)
  }, [router, searchParams, t])

  const { entries, isLoading, mutate } = useConversationHistory(
    searchQuery.trim() || undefined,
  )
  const { mutate: mutateScopedCache } = useSWRConfig()

  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [searchOpen])

  const filteredEntries = entries

  function handleCardClick(href: string) {
    router.push(href)
  }

  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await chatRoomsApi.delete(id)
      mutate()
      // The rail, home recents and trial count list the same rooms.
      revalidateConversationLists(mutateScopedCache)
      toast.success(t('conversations.deleted'))
    } catch {
      toast.error(t('conversations.deleteFailed'))
    }
  }, [mutate, mutateScopedCache, t])

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      // Optimistically reflect the new title, then confirm with the server.
      mutate(
        (current) =>
          current
            ? {
                ...current,
                items: current.items.map((e) =>
                  e.id === id ? { ...e, title } : e,
                ),
              }
            : current,
        { revalidate: false },
      )
      try {
        await chatRoomsApi.updateTitle(id, title)
        mutate()
        revalidateConversationLists(mutateScopedCache)
      } catch {
        toast.error(t('conversations.renameFailed'))
        mutate() // roll back to server state
      }
    },
    [mutate, mutateScopedCache, t],
  )

  const showInitialSkeleton = isLoading && entries.length === 0 && searchQuery.trim() === ''
  const showSearchBar = entries.length > 0 || searchQuery.trim().length > 0 || searchOpen

  if (showInitialSkeleton) {
    return (
      <div className="min-h-screen bg-background">
        <PageLayout className="max-w-none p-8 lg:p-8">
          <PageHeader
            title={<Heading level={1} className="text-2xl md:text-4xl">{t('conversations.title')}</Heading>}
            description={t('conversations.description')}
          />
          <PageContent className="gap-0">
            <div className="flex flex-col gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-4 rounded-xl border border-border bg-background p-5 sm:px-6">
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-5 w-64 max-w-full" />
                    <Skeleton className="h-3.5 w-40" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="-ml-2 h-8 w-8 rounded-full" />
                    <Skeleton className="-ml-2 h-8 w-8 rounded-full" />
                    <Skeleton className="h-3.5 w-48" />
                  </div>
                </div>
              ))}
            </div>
          </PageContent>
        </PageLayout>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <PageLayout className="max-w-none p-8 lg:p-8">
        <PageHeader
          title={(
            <Heading level={1} className="text-2xl md:text-4xl">
              {t('conversations.title')}
            </Heading>
          )}
          description={t('conversations.description')}
          action={(
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {showSearchBar && (
                <div
                  className={cn(
                    'flex h-10 items-center overflow-hidden rounded-lg border-[1.5px] border-cta bg-background/45 transition-all duration-200',
                    searchOpen || searchQuery ? 'w-[320px] px-2' : 'w-10 px-0',
                  )}
                >
                  <button
                    type="button"
                    aria-label={t('conversations.searchAria')}
                    data-testid="conversations-search-toggle"
                    onClick={() => setSearchOpen(true)}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-cta"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                  <Input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t('conversations.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="conversations-search-input"
                    className={cn(
                      'h-10 flex-1 border-0 bg-transparent px-0 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0',
                      !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
                    )}
                  />
                  <button
                    type="button"
                    aria-label={t('conversations.searchClear')}
                    data-testid="conversations-search-clear"
                    onClick={() => {
                      setSearchQuery('')
                      setSearchOpen(false)
                    }}
                    className={cn(
                      'inline-flex h-10 w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground',
                      !(searchOpen || searchQuery) && 'pointer-events-none opacity-0',
                    )}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <Button
                variant="cta"
                className="h-10 rounded-lg px-4 text-base font-medium"
                data-testid="conversations-new-room"
                onClick={() => setComposeOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('conversations.new')}
              </Button>
            </div>
          )}
        />

        <PageContent className="gap-0">
          {entries.length === 0 && !searchQuery.trim() ? (
            <LobbyEmptyState
              title={t('conversations.emptyTitle')}
              description={t('conversations.emptyBody')}
            >
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  className="rounded-md h-10 px-4 text-base"
                  data-testid="conversations-empty-new-room"
                  onClick={() => setComposeOpen(true)}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
                  {t('conversations.new')}
                </Button>
                <Button
                  variant="secondary"
                  className="rounded-md h-10 px-4 text-base"
                  data-testid="conversations-empty-chat-twin"
                  onClick={() => setComposeOpen(true)}
                >
                  <MessageSquare className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
                  {t('conversations.interviewTwin')}
                </Button>
              </div>
            </LobbyEmptyState>
          ) : (
            <>
              <Text className="pb-4 font-sans text-sm font-medium text-muted-foreground" data-testid="conversations-recent-heading">
                {t('conversations.recent')}
              </Text>
              {isLoading && filteredEntries.length === 0 && searchQuery.trim() ? (
                <div className="flex flex-col gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex flex-col gap-4 rounded-xl border border-border bg-background p-5 sm:px-6">
                      <div className="flex flex-col gap-1.5">
                        <Skeleton className="h-5 w-64 max-w-full" />
                        <Skeleton className="h-3.5 w-40" />
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <Skeleton className="-ml-2 h-8 w-8 rounded-full" />
                        <Skeleton className="-ml-2 h-8 w-8 rounded-full" />
                        <Skeleton className="h-3.5 w-48" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3" data-testid="conversations-grid">
                  {filteredEntries.map((entry) => (
                    <ConversationCard
                      key={entry.id}
                      entry={entry}
                      onClick={() => handleCardClick(entry.href)}
                      onDelete={handleDeleteConversation}
                      onRename={handleRenameConversation}
                    />
                  ))}
                </div>
              )}

              {filteredEntries.length === 0 && searchQuery.trim() && !isLoading && (
                <div className="py-12 text-center">
                  <Text color="muted">
                    {t('conversations.noMatch', { query: searchQuery })}
                  </Text>
                </div>
              )}
            </>
          )}
        </PageContent>
      </PageLayout>

      <ComposeModal
        open={composeOpen}
        onOpenChange={handleComposeOpenChange}
        initialTwin={composeInitialTwin ?? initialTwin}
        initialMessage={initialMessage}
        groupMode={groupMode}
      />
    </div>
  )
}
